import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGroups } from "@/hooks/useGroups";
import { getGroupStatusLabel, formatSwedishDate } from "@/utils/groupScheduleUtils";
import type { Group } from "@/types/groups";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface GroupsListProps {
  selectedGroup: Group | null;
  onSelectGroup: (group: Group | null) => void;
}

export const GroupsList = ({ selectedGroup, onSelectGroup }: GroupsListProps) => {
  const { groups, isLoading, currentUserId, createGroup, deleteGroup } = useGroups();
  const [isCreating, setIsCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // Keep selectedGroup in sync with updated groups data
  useEffect(() => {
    if (selectedGroup && groups) {
      const updatedGroup = groups.find(g => g.id === selectedGroup.id);
      if (updatedGroup && (
        updatedGroup.schedule_accepted_at !== selectedGroup.schedule_accepted_at ||
        updatedGroup.assigned_group !== selectedGroup.assigned_group
      )) {
        onSelectGroup(updatedGroup);
      }
    }
  }, [groups, selectedGroup, onSelectGroup]);

  const handleCreateGroup = async () => {
    const newGroup = await createGroup.mutateAsync({ name: newGroupName || undefined });
    setNewGroupName("");
    setIsCreating(false);
    // Automatically select the newly created group
    if (newGroup) {
      onSelectGroup(newGroup);
    }
  };

  const handleSelectGroup = (group: Group) => {
    if (selectedGroup?.id === group.id) {
      onSelectGroup(null);
    } else {
      onSelectGroup(group);
    }
  };

  const handleDeleteGroup = async (e: React.MouseEvent, groupId: string) => {
    e.stopPropagation();
    await deleteGroup.mutateAsync(groupId);
    if (selectedGroup?.id === groupId) {
      onSelectGroup(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mina grupper</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Laddar grupper...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Mina grupper</CardTitle>
            <CardDescription>
              Hantera dina FÖS-grupper och scheman
            </CardDescription>
          </div>
          {!isCreating && (
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Skapa ny grupp
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isCreating && (
          <div className="mb-6 p-4 border rounded-lg bg-muted/50">
            <Label htmlFor="groupName">Gruppnamn (valfritt)</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="groupName"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="T.ex. Vårgruppen 2026"
              />
              <Button 
                onClick={handleCreateGroup}
                disabled={createGroup.isPending}
              >
                {createGroup.isPending ? "Skapar..." : "Skapa"}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsCreating(false);
                  setNewGroupName("");
                }}
              >
                Avbryt
              </Button>
            </div>
          </div>
        )}

        {(!groups || groups.length === 0) ? (
          <p className="text-muted-foreground text-center py-8">
            Du har inga grupper ännu. Skapa en ny grupp för att komma igång.
          </p>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const status = getGroupStatusLabel(group);
              const isSelected = selectedGroup?.id === group.id;
              const isLinked = currentUserId && group.group_leader_id !== currentUserId;
              
              return (
                <div
                  key={group.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected 
                      ? "border-primary bg-primary/5" 
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => handleSelectGroup(group)}
                >
                    <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">
                        {group.name || `Grupp ${group.id.slice(0, 8)}`}
                      </h4>
                      {group.first_possible_date && (
                        <p className="text-sm text-muted-foreground">
                          Startdatum: {formatSwedishDate(new Date(group.first_possible_date))}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isLinked && (
                        <Badge variant="secondary">Kopplad kollega</Badge>
                      )}
                      <Badge variant={status.variant}>{status.label}</Badge>
                      {!isLinked && !group.schedule_accepted_at && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Ta bort grupp?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Är du säker på att du vill ta bort denna grupp? Detta kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={(e) => handleDeleteGroup(e, group.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Ta bort
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
